
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

interface RouteProtectorProps {
  children: React.ReactNode;
}

const RouteProtector: React.FC<RouteProtectorProps> = ({ children }) => {
  const [isRouteReady, setIsRouteReady] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const validateRoute = async () => {
      try {
        // Ensure scroll position is at top before route validation
        window.scrollTo(0, 0);
        
        // Only log in development mode to prevent information leakage
        if (import.meta.env.DEV) {
          console.log(`[RouteProtector] Validating route: ${location.pathname}`);
        }
        
        // Check if critical components are available
        if (!document.getElementById('root')) {
          throw new Error('Root element not found');
        }

        // Small delay to ensure smooth transitions
        await new Promise(resolve => setTimeout(resolve, 50));
        
        setIsRouteReady(true);
        
        if (import.meta.env.DEV) {
          console.log(`[RouteProtector] Route validated: ${location.pathname}`);
        }
      } catch (error) {
        // Only log errors in development, in production use error monitoring service
        if (import.meta.env.DEV) {
          console.error('[RouteProtector] Route validation failed:', error);
        }
        // Still allow the route to load, but log the error
        setIsRouteReady(true);
      }
    };

    setIsRouteReady(false);
    validateRoute();
  }, [location.pathname]);

  if (!isRouteReady) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="mb-4">
            <img 
              src="/lovable-uploads/76503acc-0290-4b3e-ae3b-f6ab214ab2ce.png" 
              alt="Flamingo" 
              className="h-8 mx-auto mb-4"
            />
          </div>
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-primary" />
          <p className="text-slate-400 text-sm">Loading page...</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default RouteProtector;
